import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { signIn, signOut, getCurrentUser, confirmSignIn, resetPassword, confirmResetPassword, cognitoUserPoolsTokenProvider, fetchUserAttributes, signUp, confirmSignUp, resendSignUpCode, autoSignIn } from 'aws-amplify/auth/cognito';
import { sessionStorage, defaultStorage } from 'aws-amplify/utils';
import { useQueryClient } from '@tanstack/react-query';
import { parseAuthError, AUTH_ERROR_CODES } from '../utils/errorHandlers';
import { PASSWORD_POLICY_MESSAGE } from '../utils/passwordPolicy';
import { trackSignUp } from '../utils/analytics';
import {
  BOT_CHECK_FAILED_MESSAGE,
  CONFIRM_SIGN_UP_REQUIRED,
  NEW_PASSWORD_REQUIRED,
  PASSWORD_RESET_REQUIRED,
} from '../constants';
import { fetchTermsStatus, recordTermsAcceptance, ApiError } from '../services/reportsApi';

interface User {
  id: string;
  email: string;
  name: string;
}

/**
 * Terms gate (task 05). 'clear': nothing to accept. 'required': the user must
 * accept the current Terms and Privacy Policy. 'unverified': the status check
 * failed, so the app neither proceeds nor asks for consent it may not save.
 */
export type TermsStatus = 'clear' | 'required' | 'unverified';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  // For admin-managed users with temp passwords
  setNewPassword?: (newPassword: string) => Promise<void>;
  needsNewPassword?: boolean;
  // Password reset functions
  initiatePasswordReset: (email: string) => Promise<void>;
  completePasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  // Self-serve signup (task 08)
  registerAccount: (
    email: string,
    password: string,
    termsAccepted: boolean,
    turnstileToken?: string | null
  ) => Promise<void>;
  confirmRegistration: (email: string, code: string) => Promise<'SIGNED_IN' | 'CONFIRMED'>;
  resendRegistrationCode: (email: string) => Promise<void>;
  // Terms gate (task 05). ProtectedRoute shows TermsAcceptance unless 'clear'.
  termsStatus: TermsStatus;
  acceptTerms: () => Promise<void>;
  recheckTerms: () => Promise<void>;
}

// Type guard for Amplify errors which have name and message properties
interface AmplifyError extends Error {
  name: string;
}

function isAmplifyError(error: unknown): error is AmplifyError {
  return error instanceof Error && 'name' in error;
}

// Every email is trimmed and lowercased before it reaches Cognito, so sign-in,
// password reset and signup all use the same string whatever case was typed.
// The stored email is the report owner key. Plain lowercasing, not the backend's
// normalise_email: plus-tags and Gmail dots are real, distinct accounts.
const toCognitoEmail = (email: string) => email.trim().toLowerCase();

const TOO_MANY_ATTEMPTS_MESSAGE = 'Too many attempts. Please try again later.';
const CODE_MISMATCH_MESSAGE = 'Invalid verification code. Please check and try again.';
const EXPIRED_CODE_MESSAGE = 'Verification code has expired. Please request a new code.';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

function createUserFromEmail(userId: string, email: string): User {
  const name = email.split('@')[0]
    .replace(/[^a-zA-Z]/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase()) || 'User';

  return {
    id: userId,
    email,
    name
  };
}

// Log-safe description of a terms-check failure: a code or status, never a
// message or body. parseError would collapse every 5xx to SERVER_ERROR.
const describeTermsError = (error: unknown): string =>
  error instanceof ApiError ? (error.code ?? String(error.status))
    : error instanceof Error ? error.name : 'unknown';

const isSessionError = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 401;

function configureTokenStorage(persistent: boolean) {
  // Use defaultStorage (localStorage) for persistent sessions, sessionStorage otherwise
  cognitoUserPoolsTokenProvider.setKeyValueStorage(persistent ? defaultStorage : sessionStorage);
}

async function getCurrentUserFromEitherStorage() {
  // On a fresh page load the token provider points at its default storage
  // (localStorage), but a no-remember-me session lives in sessionStorage —
  // which survives refreshes. Restore must therefore look in BOTH storages,
  // leaving the provider pointed at whichever one held the session; otherwise
  // a refresh logs out every no-remember-me user despite their live tokens.
  configureTokenStorage(true);
  try {
    return await getCurrentUser();
  } catch {
    configureTokenStorage(false);
    return await getCurrentUser(); // throws to the caller when neither storage has a session
  }
}

async function clearStrayTokenStorage() {
  // signOut() only clears the storage the token provider currently points at,
  // so sign out under EACH storage. This guarantees a prior remember-me
  // (localStorage) session can't survive a later no-remember-me (sessionStorage)
  // login. Each signOut is swallowed: a missing session is the normal clean case.
  for (const storage of [defaultStorage, sessionStorage]) {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(storage);
    try {
      await signOut();
    } catch {
      // No active session in this storage — expected on a clean login.
    }
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // One QueryClient serves the whole tab and its keys are not user-scoped, so
  // every sign-in and sign-out empties it. Otherwise the next account is served
  // the previous one's reports and balance from cache (FE-DATA-01).
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [tempEmail, setTempEmail] = useState<string>('');
  const [termsStatus, setTermsStatus] = useState<TermsStatus>('clear');
  // Set by registerAccount when the signup box was ticked, consumed by the
  // auto sign-in in confirmRegistration. In memory only: a reload or the
  // Login handoff loses it, and the login gate asks once more.
  const termsTickedAtSignup = useRef(false);

  /**
   * Resolve the terms gate. Awaited BEFORE setUser at every sign-in entry
   * point, so ProtectedRoute never renders the app for a frame before the
   * prompt. Never throws.
   *
   * Returns the status to commit, or 'signed-out' when the API rejected the
   * session itself (401): cached tokens that cannot be refreshed. Callers
   * treat that as no session rather than an unanswerable "try again".
   */
  const checkTerms = useCallback(async (): Promise<TermsStatus | 'signed-out'> => {
    try {
      const { acceptanceRequired } = await fetchTermsStatus();
      return acceptanceRequired ? 'required' : 'clear';
    } catch (error) {
      if (isSessionError(error)) return 'signed-out';
      console.error('Terms status check failed:', describeTermsError(error));
      return 'unverified';
    }
  }, []);

  // The auto sign-in half of the gate. With the signup box ticked, record
  // acceptance instead of asking again; without it, the ordinary check.
  const settleTermsAfterSignup = useCallback(async (): Promise<TermsStatus> => {
    if (!termsTickedAtSignup.current) {
      const terms = await checkTerms();
      return terms === 'signed-out' ? 'unverified' : terms;
    }
    termsTickedAtSignup.current = false;
    try {
      await recordTermsAcceptance();
      return 'clear';
    } catch (error) {
      // The prompt asks once more, and its Continue retries the write.
      console.error('Recording terms acceptance failed:', describeTermsError(error));
      return 'required';
    }
  }, [checkTerms]);

  useEffect(() => {
    // Check for existing Cognito session on app startup
    const checkAuthStatus = async () => {
      try {
        const currentUser = await getCurrentUserFromEitherStorage();
        // signInDetails is only available after fresh sign-in, not on session restore
        let userEmail = currentUser.signInDetails?.loginId;

        if (!userEmail) {
          // Fallback: fetch user attributes to get email for restored sessions
          const attributes = await fetchUserAttributes();
          userEmail = attributes.email;
        }

        if (!userEmail) {
          throw new Error('User email not found in Cognito session');
        }
        const terms = await checkTerms();
        if (terms === 'signed-out') {
          throw new Error('Session could not be refreshed');
        }
        setTermsStatus(terms);
        setUser(createUserFromEmail(currentUser.userId, userEmail));
      } catch (error) {
        // User not signed in
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, [checkTerms]);

  const login = useCallback(async (rawEmail: string, password: string, rememberMe = false): Promise<void> => {
    const email = toCognitoEmail(rawEmail);
    setIsLoading(true);

    // Clear any stray session from BOTH token storages before configuring the
    // target storage. Amplify v6 signIn() throws UserAlreadyAuthenticatedException
    // if a token exists, and signOut() only clears the currently-selected storage —
    // so a remember-me (localStorage) session must be cleared even when this login
    // uses sessionStorage. See task 4-Low-rememberme-stale-tokens.
    await clearStrayTokenStorage();
    // Also covers setNewPassword, which completes a challenge this call began.
    queryClient.clear();

    // Configure token storage for the NEW session based on remember-me preference.
    // NOTE: clearStrayTokenStorage() leaves the token provider pointed at whichever
    // storage it visited last (sessionStorage), so this configure call is MANDATORY
    // and must stay AFTER the clear. Reordering would leave storage on sessionStorage
    // regardless of rememberMe.
    configureTokenStorage(rememberMe);

    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password: password
      });

      if (isSignedIn) {
        const currentUser = await getCurrentUser();
        const terms = await checkTerms();
        // A 401 straight after a fresh signIn is not a stale session; show
        // "couldn't check" rather than bouncing the user back to the form.
        setTermsStatus(terms === 'signed-out' ? 'unverified' : terms);
        setUser(createUserFromEmail(currentUser.userId, email));
      } else if (nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        // Unconfirmed account: Login shows the confirm-code step on this sentinel.
        // Amplify converts UserNotConfirmedException into this step, so signIn
        // never throws it here.
        throw new Error(CONFIRM_SIGN_UP_REQUIRED);
      } else if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // Admin created user - needs to set new password
        setNeedsNewPassword(true);
        setTempEmail(email);
        throw new Error(NEW_PASSWORD_REQUIRED); // Special error code for UI
      } else if (nextStep?.signInStep === 'RESET_PASSWORD') {
        // Support ran admin-reset-user-password: Amplify turns
        // PasswordResetRequiredException into this step. Cognito has already
        // emailed a code, so Login goes straight to the reset-code step.
        throw new Error(PASSWORD_RESET_REQUIRED);
      } else {
        // Any other step (MFA, for one) would otherwise resolve, and the user
        // would be bounced back to the form with no message. Log only the
        // step name: it carries no user data.
        console.error('Unhandled sign-in step:', nextStep?.signInStep);
        throw new Error('Additional sign-in verification is required. Please contact support.');
      }
    } catch (error) {
      console.error('Login failed:', parseAuthError(error).code);
      throw new Error(error instanceof Error ? error.message : 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  }, [checkTerms, queryClient]);

  const logout = useCallback(async () => {
    // Reset the terms gate too, or it leaks into the next session in this tab.
    try {
      await signOut();
      setUser(null);
      setTermsStatus('clear');
      termsTickedAtSignup.current = false;
      queryClient.clear();
    } catch (error) {
      console.error('Logout failed:', parseAuthError(error).code);
      setUser(null); // Force logout on error
      setTermsStatus('clear');
      termsTickedAtSignup.current = false;
      queryClient.clear();
    }
  }, [queryClient]);

  const setNewPassword = useCallback(async (newPassword: string): Promise<void> => {
    setIsLoading(true);
    try {
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: newPassword
      });

      if (isSignedIn) {
        const currentUser = await getCurrentUser();
        // Admin-created users reach a signed-in state here, not through login().
        const terms = await checkTerms();
        setTermsStatus(terms === 'signed-out' ? 'unverified' : terms);
        setUser(createUserFromEmail(currentUser.userId, tempEmail));
        setNeedsNewPassword(false);
        setTempEmail('');
      }
    } catch (error) {
      console.error('Set new password failed:', parseAuthError(error).code);
      throw new Error(error instanceof Error ? error.message : 'Failed to set new password.');
    } finally {
      setIsLoading(false);
    }
  }, [checkTerms, tempEmail]);

  const initiatePasswordReset = useCallback(async (email: string): Promise<void> => {
    try {
      await resetPassword({ username: toCognitoEmail(email) });
    } catch (error) {
      console.error('Password reset initiation failed:', parseAuthError(error).code);
      // Use generic message to not reveal if email exists
      if (isAmplifyError(error)) {
        if (error.name === 'UserNotFoundException') {
          throw new Error('If an account exists with this email, you will receive a verification code.');
        }
        if (error.name === 'LimitExceededException') {
          throw new Error('Too many attempts. Please try again later.');
        }
      }
      throw new Error('Unable to process request. Please try again.');
    }
  }, []);

  const completePasswordReset = useCallback(async (email: string, code: string, newPassword: string): Promise<void> => {
    try {
      await confirmResetPassword({
        username: toCognitoEmail(email),
        confirmationCode: code,
        newPassword: newPassword
      });
    } catch (error) {
      console.error('Password reset confirmation failed:', parseAuthError(error).code);
      if (isAmplifyError(error)) {
        if (error.name === 'CodeMismatchException') {
          throw new Error(CODE_MISMATCH_MESSAGE);
        }
        if (error.name === 'ExpiredCodeException') {
          throw new Error(EXPIRED_CODE_MESSAGE);
        }
        if (error.name === 'InvalidPasswordException') {
          throw new Error(PASSWORD_POLICY_MESSAGE);
        }
        if (error.name === 'LimitExceededException') {
          throw new Error('Too many attempts. Please try again later.');
        }
      }
      throw new Error('Unable to reset password. Please try again.');
    }
  }, []);

  const registerAccount = useCallback(async (
    rawEmail: string,
    password: string,
    termsAccepted: boolean,
    turnstileToken?: string | null
  ): Promise<void> => {
    termsTickedAtSignup.current = termsAccepted;
    const email = toCognitoEmail(rawEmail);
    // Auto sign-in runs an ordinary signIn after confirmation, which throws if
    // tokens already exist, so clear both storages first. Session-only storage
    // for the new session: the signup form has no remember-me box.
    await clearStrayTokenStorage();
    // Also covers the auto sign-in in confirmRegistration, which follows this.
    queryClient.clear();
    configureTokenStorage(false);

    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: { email },
          autoSignIn: true,
          // Read by the Pre Sign-up bot check. Absent when no widget is
          // configured, so that call is unchanged.
          ...(turnstileToken ? { validationData: { turnstileToken } } : {}),
        },
      });
    } catch (error) {
      console.error('Sign up failed:', parseAuthError(error).code);
      if (isAmplifyError(error)) {
        switch (error.name) {
          case AUTH_ERROR_CODES.USERNAME_EXISTS: {
            // Signup checks .name, not the message, to try the resend-and-confirm recovery.
            const exists = new Error('An account with this email already exists. Sign in instead.');
            exists.name = AUTH_ERROR_CODES.USERNAME_EXISTS;
            throw exists;
          }
          case AUTH_ERROR_CODES.INVALID_PASSWORD:
            throw new Error(PASSWORD_POLICY_MESSAGE);
          case AUTH_ERROR_CODES.INVALID_PARAMETER:
            throw new Error('Please enter a valid email address.');
          case AUTH_ERROR_CODES.CODE_DELIVERY_FAILURE:
            throw new Error("We couldn't send a verification email to that address. Check it and try again.");
          case AUTH_ERROR_CODES.NOT_AUTHORIZED:
            // Cognito's answer when the pool is admin-create-only (SelfSignup off).
            throw new Error("New account signup isn't open right now. Please check back soon.");
          case AUTH_ERROR_CODES.LIMIT_EXCEEDED:
          case AUTH_ERROR_CODES.TOO_MANY_REQUESTS:
            throw new Error(TOO_MANY_ATTEMPTS_MESSAGE);
          case AUTH_ERROR_CODES.USER_LAMBDA_VALIDATION:
            // The Pre Sign-up bot check refused the signup (or, rarely, the
            // trigger timed out). Never show Cognito's own message
            // ("PreSignUp failed ...").
            throw new Error(BOT_CHECK_FAILED_MESSAGE);
          case AUTH_ERROR_CODES.UNEXPECTED_LAMBDA:
          case AUTH_ERROR_CODES.INVALID_LAMBDA_RESPONSE:
            throw new Error('Something went wrong creating your account. Please try again in a moment.');
        }
      }
      throw new Error('Unable to create your account. Please try again.');
    }
  }, [queryClient]);

  const confirmRegistration = useCallback(async (rawEmail: string, code: string): Promise<'SIGNED_IN' | 'CONFIRMED'> => {
    const email = toCognitoEmail(rawEmail);
    let signUpStep: string;
    try {
      const { nextStep } = await confirmSignUp({ username: email, confirmationCode: code });
      signUpStep = nextStep.signUpStep;
    } catch (error) {
      console.error('Sign up confirmation failed:', parseAuthError(error).code);
      if (isAmplifyError(error)) {
        switch (error.name) {
          case AUTH_ERROR_CODES.CODE_MISMATCH:
            throw new Error(CODE_MISMATCH_MESSAGE);
          case AUTH_ERROR_CODES.EXPIRED_CODE:
            throw new Error(EXPIRED_CODE_MESSAGE);
          case AUTH_ERROR_CODES.LIMIT_EXCEEDED:
          case AUTH_ERROR_CODES.TOO_MANY_FAILED_ATTEMPTS:
            throw new Error(TOO_MANY_ATTEMPTS_MESSAGE);
          case AUTH_ERROR_CODES.NOT_AUTHORIZED:
            // Cognito's answer for an account that is already confirmed.
            return 'CONFIRMED';
          case AUTH_ERROR_CODES.USER_LAMBDA_VALIDATION:
          case AUTH_ERROR_CODES.UNEXPECTED_LAMBDA:
          case AUTH_ERROR_CODES.INVALID_LAMBDA_RESPONSE:
            // A Post Confirmation trigger failure or timeout surfaces here, and
            // AWS does not say whether the account confirmed. Sending the user to
            // sign in self-corrects: an unconfirmed account is handed back to the
            // confirm step.
            return 'CONFIRMED';
        }
      }
      throw new Error('Unable to verify your email. Please try again.');
    }

    // Cognito just confirmed a new account: the one unambiguous point for
    // GA's sign_up (task 26). The catch above returns early for an account
    // that was already confirmed and for trigger errors, and login or a
    // reload never come here. It covers the signup page and Login's
    // unconfirmed-account handoff alike.
    trackSignUp();

    if (signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
      // Task 05: the fourth sign-in entry point, alongside login(),
      // checkAuthStatus() and setNewPassword(). The box was ticked on the form
      // step of this same page, so record acceptance now rather than asking
      // again. Without the tick, fall back to the ordinary check.
      try {
        const { isSignedIn } = await autoSignIn();
        if (isSignedIn) {
          const currentUser = await getCurrentUser();
          setTermsStatus(await settleTermsAfterSignup());
          setUser(createUserFromEmail(currentUser.userId, email));
          return 'SIGNED_IN';
        }
      } catch (error) {
        console.error('Auto sign-in failed:', parseAuthError(error).code);
      }
    }
    // The autoSignIn state is in memory, lasts three minutes and is reset by any
    // signIn, so the Login handoff and a page reload both land here.
    return 'CONFIRMED';
  }, [settleTermsAfterSignup]);

  // Called from TermsAcceptance. Throws so the screen can show the error
  // inline; ToastProvider sits inside AuthProvider, so no toast from here.
  const acceptTerms = useCallback(async (): Promise<void> => {
    await recordTermsAcceptance();
    setTermsStatus('clear');
  }, []);

  // Retry for the 'unverified' screen. A 401 here means the session died
  // while the screen was up: sign out rather than loop.
  const recheckTerms = useCallback(async (): Promise<void> => {
    const terms = await checkTerms();
    if (terms === 'signed-out') {
      await logout();
      return;
    }
    setTermsStatus(terms);
  }, [checkTerms, logout]);

  const resendRegistrationCode = useCallback(async (email: string): Promise<void> => {
    try {
      await resendSignUpCode({ username: toCognitoEmail(email) });
    } catch (error) {
      console.error('Resend sign up code failed:', parseAuthError(error).code);
      if (isAmplifyError(error)) {
        if (error.name === AUTH_ERROR_CODES.LIMIT_EXCEEDED) {
          throw new Error(TOO_MANY_ATTEMPTS_MESSAGE);
        }
        if (error.name === AUTH_ERROR_CODES.CODE_DELIVERY_FAILURE) {
          throw new Error("We couldn't send a verification email to that address.");
        }
      }
      throw new Error('Unable to send a new code. Please try again.');
    }
  }, []);

  // Memoised so useAuth consumers re-render only when auth state changes. No
  // consumer relies on the value or a handler changing identity.
  const value = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    setNewPassword,
    needsNewPassword,
    initiatePasswordReset,
    completePasswordReset,
    registerAccount,
    confirmRegistration,
    resendRegistrationCode,
    termsStatus,
    acceptTerms,
    recheckTerms
  }), [
    user,
    isLoading,
    login,
    logout,
    setNewPassword,
    needsNewPassword,
    initiatePasswordReset,
    completePasswordReset,
    registerAccount,
    confirmRegistration,
    resendRegistrationCode,
    termsStatus,
    acceptTerms,
    recheckTerms,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};